import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Skeleton } from "@/components/ui/Skeleton";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { EmptyState } from "@/components/ui/EmptyState";
import { useInvoices, useInvoice } from "@/hooks/useInvoices";
import { useApiMutation } from "@/lib/api";
import { useToast } from "@/hooks/useToast";
import { formatPLN } from "@/lib/format";
import { BillingPeriod, LineItem } from "@/types/billing";
import { TenantList } from "./TenantList";
import { LineItemsTable } from "./LineItemsTable";
import { AddLineItemModal, LineItemFormData } from "./AddLineItemModal";

export function InvoicingView({ period, refetch, location = null }: { period: BillingPeriod; refetch: () => void; location?: string | null }) {
  const { data: invoices, refetch: refetchInvoices, loading } = useInvoices(period.period_id);
  const [selected, setSelected] = useState<string | null>(null);
  const { data: invoice, refetch: refetchOne } = useInvoice(period.period_id, selected);

  const visibleInvoices = location ? (invoices?.filter((i) => i.location === location) ?? []) : (invoices ?? []);

  useEffect(() => {
    if (invoices && invoices.length > 0) {
      const inView = location ? invoices.filter((i) => i.location === location) : invoices;
      if (!selected || !inView.find((i) => i.agreement_id === selected)) {
        setSelected(inView[0]?.agreement_id ?? null);
      }
    }
  }, [invoices, location]);

  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<LineItem | null>(null);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<LineItem | null>(null);
  const { mutate } = useApiMutation();
  const { push } = useToast();

  async function reopen() {
    try {
      await mutate("/api/billing/period/reopen", "POST");
      push({ message: "Wrócono do inkasowania", tone: "success" });
      setReopenOpen(false);
      refetch();
    } catch (err) {
      push({ message: (err as Error).message, tone: "danger" });
    }
  }

  async function closeMonth() {
    try {
      await mutate(`/api/invoice/${period.period_id}/finalize`, "POST");
      push({ message: "Miesiąc zamknięty", tone: "success" });
      setCloseOpen(false);
      refetch();
    } catch (err) {
      push({ message: (err as Error).message, tone: "danger" });
    }
  }

  async function addLine(data: LineItemFormData) {
    if (!selected) return;
    await mutate(`/api/invoice/${period.period_id}/${selected}/line-item`, "POST", data);
    push({ message: "Dodano pozycję", tone: "success" });
    refetchOne();
    refetchInvoices();
  }

  async function editLine(item: LineItem, data: LineItemFormData) {
    if (!selected) return;
    const force = item.editable ? "" : "?force=true";
    await mutate(`/api/invoice/${period.period_id}/${selected}/line-item/${item.item_id}${force}`, "PUT", data);
    push({ message: "Zaktualizowano pozycję", tone: "success" });
    refetchOne();
    refetchInvoices();
  }

  function requestDelete(item: LineItem) {
    if (item.editable) {
      deleteLine(item);
    } else {
      setDeleteTarget(item);
    }
  }

  async function deleteLine(item: LineItem) {
    if (!selected) return;
    const force = item.editable ? "" : "?force=true";
    try {
      await mutate(`/api/invoice/${period.period_id}/${selected}/line-item/${item.item_id}${force}`, "DELETE");
      push({ message: "Usunięto pozycję", tone: "success" });
      setDeleteTarget(null);
      refetchOne();
      refetchInvoices();
    } catch (err) {
      push({ message: (err as Error).message, tone: "danger" });
    }
  }

  if (loading || !invoices) return <Skeleton className="h-64 w-full" />;
  if (invoices.length === 0) return <EmptyState title="Brak faktur w tym okresie" />;

  return (
    <>
      <div className="flex justify-end gap-2 mb-4">
        <Button variant="secondary" onClick={() => setReopenOpen(true)}>Otwórz inkasowanie ◀</Button>
        <Button onClick={() => setCloseOpen(true)}>Zakończ fakturowanie ▶</Button>
      </div>

      <div className="flex flex-col lg:flex-row gap-4">
        <TenantList invoices={visibleInvoices} selectedId={selected} onSelect={setSelected} showStatus />
        <div className="flex-1 min-w-0">
          {invoice ? (
            <Card padding="none">
              <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold">{invoice.tenant_name}</div>
                  <div className="text-xs text-text-muted capitalize">{invoice.location} · {invoice.room}</div>
                </div>
                {invoice.status !== "sent" && (
                  <Button size="sm" variant="secondary" iconLeft={<Plus className="size-3.5" />} onClick={() => { setEditing(null); setAddOpen(true); }}>
                    Dodaj pozycję
                  </Button>
                )}
              </div>
              {invoice.status === "sent" && (
                <div className="px-4 py-2 bg-bg-subtle text-xs text-text-muted border-b border-border">
                  Ta faktura została już wysłana — edycja zablokowana, żeby nie rozjechała się z tym, co dostał najemca.
                </div>
              )}
              <LineItemsTable
                items={invoice.line_items}
                onEdit={invoice.status === "sent" ? undefined : (it) => { setEditing(it); setAddOpen(true); }}
                onDelete={invoice.status === "sent" ? undefined : requestDelete}
              />
              <div className="px-4 py-3 border-t border-border flex justify-between text-sm">
                <span className="text-text-muted">Suma</span>
                <span className="font-mono font-semibold">{formatPLN(invoice.total_amount)}</span>
              </div>
            </Card>
          ) : (
            <Skeleton className="h-64 w-full" />
          )}
        </div>
      </div>

      <AddLineItemModal
        open={addOpen}
        initial={editing ?? undefined}
        agreementId={selected}
        location={invoice?.location ?? null}
        onClose={() => { setAddOpen(false); setEditing(null); }}
        onSubmit={editing ? (d) => editLine(editing, d) : addLine}
      />

      <ConfirmDialog
        open={reopenOpen}
        onClose={() => setReopenOpen(false)}
        onConfirm={reopen}
        title="Wrócić do inkasowania?"
        confirmLabel="Wróć"
        description="Wszystkie pozycje wyliczone z flatMngt-balance zostaną odrzucone. Pozycje ręczne są zachowane."
      />
      <ConfirmDialog
        open={closeOpen}
        onClose={() => setCloseOpen(false)}
        onConfirm={closeMonth}
        title="Zakończyć fakturowanie?"
        confirmLabel="Zakończ"
        description="Wszystkie szkice zostaną sfinalizowane i przejdą do etapu wysyłki."
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteLine(deleteTarget)}
        title="Usunąć pozycję naliczoną automatycznie?"
        confirmLabel="Usuń"
        description="Ta pozycja pochodzi z automatycznego rozliczenia (media / czynsz). Usunięcie zmieni tylko tę fakturę — nie skasuje ani nie przeliczy danych źródłowych w zakładce Rozliczenie/Odczyty."
      />
    </>
  );
}
